import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ContextStore from '../../hooks/ContextStore';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';

// Pack / bundle definition + per-product minimum profit (Auto-Pilot floor) for one product.
interface Comp { group: string; qty: number; name: string; imageUrl?: string | null; buyingRate?: number }
interface PackData {
  groupId: string; name: string; isPack: boolean; minProfitOverride: number | null;
  costs: { buyingRate: number; transportFees: number; storageFees: number; packageFees: number };
  components: Comp[]; availability: { available: number; counted: boolean } | null;
  suggestions: { group: string; name: string; imageUrl?: string | null; score: number }[];
}
interface GroupOption { _id: string; name: string }

const isErr = (r: any) => !r || r.error || r.isAxiosError;
const box: React.CSSProperties = { border: '1px solid #E5E7EB', borderRadius: 10, padding: '0.75rem 0.9rem', background: '#fff' };

export const PackFloorSection: FC<{ groupId: string; allGroups: GroupOption[] }> = ({ groupId, allGroups }) => {
  const { contextStore } = useContext(ContextStore);
  const token = contextStore.token;
  const [data, setData] = useState<PackData | null>(null);
  const [isPack, setIsPack] = useState(false);
  const [comps, setComps] = useState<Comp[]>([]);
  const [floor, setFloor] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    const r = await dispatch(actions.inventoryPackGet, {}, token, { groupId });
    if (isErr(r)) return;
    setData(r); setIsPack(r.isPack); setComps(r.components); setFloor(r.minProfitOverride == null ? '' : String(r.minProfitOverride));
  }, [groupId, token]);
  useEffect(() => { load(); }, [load]);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return allGroups.filter(g => g._id !== groupId && g.name?.toLowerCase().includes(q) && !comps.some(c => c.group === g._id)).slice(0, 8);
  }, [search, allGroups, comps, groupId]);

  const add = (g: { group?: string; _id?: string; name: string }) => {
    const id = g.group || g._id!;
    if (comps.some(c => c.group === id)) return;
    setComps([...comps, { group: id, qty: 1, name: g.name }]); setSearch('');
  };
  const savePack = async () => {
    setBusy(true); setMsg(null);
    const r = await dispatch(actions.inventoryPackSave, { isPack, components: comps.map(c => ({ group: c.group, qty: c.qty })) }, token, { groupId });
    setBusy(false);
    if (isErr(r)) setMsg({ ok: false, text: r?.response?.data?.error?.message || r?.error?.message || 'Could not save' });
    else { setData(r); setComps(r.components); setMsg({ ok: true, text: isPack ? `Saved — ${r.availability?.available ?? 0} pack(s) available from the products inside; costs updated.` : 'Saved — no longer a pack.' }); }
  };
  const saveFloor = async () => {
    setBusy(true); setMsg(null);
    const r = await dispatch(actions.inventoryMinProfit, { minProfit: floor === '' ? null : Number(floor) }, token, { groupId });
    setBusy(false);
    if (isErr(r)) setMsg({ ok: false, text: r?.response?.data?.error?.message || 'Could not save' });
    else {
      setData({ ...data!, minProfitOverride: floor === '' ? null : Number(floor) });
      window.dispatchEvent(new Event('own-floor-changed'));
      setMsg({ ok: true, text: floor === '' ? 'The global rules apply to this product again (Jumia 25 MAD, Marjane 40 MAD).' : `Saved. Every listing of this product, Jumia and Marjane, keeps at least ${floor} MAD profit. Listings under it are being raised now (a few minutes).` });
    }
  };

  if (!data) return null;
  const packChanged = isPack !== data.isPack || JSON.stringify(comps.map(c => [c.group, c.qty])) !== JSON.stringify(data.components.map(c => [c.group, c.qty]));
  const floorChanged = (floor === '' ? null : Number(floor)) !== data.minProfitOverride;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem', marginTop: '0.75rem' }}>
      {/* Pack */}
      <div style={box}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>📦 Pack / bundle</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={isPack} onChange={e => setIsPack(e.target.checked)} /> This product is a pack
          </label>
        </div>
        <div style={{ fontSize: '0.74rem', color: '#6B7280', margin: '4px 0 8px' }}>
          A pack has no stock of its own: its stock is what the products inside allow, its orders use their units and its cost is theirs.
        </div>
        {isPack && (
          <>
            {comps.map((c, i) => (
              <div key={c.group} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderTop: i ? '1px solid #F3F4F6' : 'none' }}>
                <input type="number" min={1} value={c.qty} style={{ width: 52 }} className="ic__adj-input"
                  onChange={e => setComps(comps.map(x => x.group === c.group ? { ...x, qty: Math.max(1, parseInt(e.target.value, 10) || 1) } : x))} />
                <span style={{ fontSize: '0.8rem', flex: 1 }}>× {c.name}</span>
                <button className="ic__topbar-btn" onClick={() => setComps(comps.filter(x => x.group !== c.group))}>✕</button>
              </div>
            ))}
            <input className="ic__adj-input" style={{ width: '100%', marginTop: 6 }} placeholder="Add a product inside the pack (type its name)…" value={search} onChange={e => setSearch(e.target.value)} />
            {matches.map(m => <div key={m._id} onClick={() => add(m)} style={{ fontSize: '0.78rem', padding: '4px 6px', cursor: 'pointer', borderRadius: 6, background: '#F9FAFB', marginTop: 3 }}>+ {m.name}</div>)}
            {!comps.length && data.suggestions.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: '0.74rem', color: '#6B7280' }}>Suggested from the name — click to add:</div>
                {data.suggestions.map(sg => <div key={sg.group} onClick={() => add(sg)} style={{ fontSize: '0.78rem', padding: '4px 6px', cursor: 'pointer', borderRadius: 6, background: '#F0FDF4', marginTop: 3 }}>+ {sg.name}</div>)}
              </div>
            )}
          </>
        )}
        {data.isPack && data.availability && (
          <div style={{ fontSize: '0.78rem', marginTop: 8, color: '#374151' }}>
            Available now: <b>{data.availability.available}</b> pack(s) · cost {Math.round(data.costs.buyingRate)} MAD (+ fees {Math.round(data.costs.transportFees + data.costs.storageFees + data.costs.packageFees)} MAD)
          </div>
        )}
        {packChanged && <button className="ic__topbar-btn ic__topbar-btn--primary" style={{ marginTop: 8 }} disabled={busy || (isPack && !comps.length)} onClick={savePack}>{busy ? 'Saving…' : 'Save pack'}</button>}
      </div>
      {/* Floor */}
      <div style={box}>
        <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>🛡 This product's own floor</div>
        <div style={{ fontSize: '0.74rem', color: '#6B7280', margin: '4px 0 8px' }}>
          Minimum profit per sale, everywhere: Jumia, Marjane, Auto-Pilot, new copies and manual price changes. It beats every global rule (Jumia 25, Marjane 40, slow sellers 15). Leave empty to use the global rules.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="number" min={0} className="ic__adj-input" style={{ width: 90 }} placeholder="global" value={floor} onChange={e => setFloor(e.target.value)} />
          <span style={{ fontSize: '0.8rem', color: '#374151' }}>MAD per sale</span>
          {floorChanged && <button className="ic__topbar-btn ic__topbar-btn--primary" disabled={busy} onClick={saveFloor}>{busy ? 'Saving…' : 'Save'}</button>}
        </div>
      </div>
      {msg && <div style={{ gridColumn: '1 / -1', fontSize: '0.78rem', color: msg.ok ? '#047857' : '#B91C1C' }}>{msg.text}</div>}
    </div>
  );
};
